import { ref, watch } from '@vue/composition-api';
import { usePromise } from 'common-module/src/util/usePromise';
import { isNil } from 'lodash';

import http from 'common-module/src/util/http';
import axios from 'axios';

import {
  RequestConfig,
  RequestParam,
  RequestFunction
} from './types';

/**
 * @param api api 请求地址
 * @param param 请求参数
 * @returns
 */
const httpRequest = async (
  api: string,
  param: RequestParam,
  config?: RequestConfig
) => {
  return http.post(api, param, config);
};

/**
 * @param requestFn 请求函数，接收promise
 * @param maxRetryCount 重新请求最大次数
 * @returns
 */
export function useRequestServe({
  request,
  requestApi,
  maxRetryCount,
  retryInterval = 3000,
  requestParam = {},
  requestConfig = {},
  showErrorAlert = false
}: {
  request: RequestFunction,
  requestApi: string,
  maxRetryCount: number,
  retryInterval: number,
  requestParam?: RequestParam,
  requestConfig?: RequestConfig,
  showErrorAlert?: boolean
}) {
  if (isNil(request) && isNil(requestApi)) {
    throw new Error('Parameter error');
  }

  const state = ref<'success' | 'fail' | 'pending'>('pending');

  const cancelSource = ref();
  const retryCount = ref<number>((maxRetryCount || 0));
  const isRetrying = ref<boolean>(false);

  const onLoad = async () => {
    onCancel();
    cancelSource.value = axios.CancelToken.source();
    state.value = 'pending';

    // 传递promise请求的
    const config: RequestConfig = {
      showError: showErrorAlert,
      ...requestConfig,
      cancelToken: cancelSource.value.token
    };
    try {
      const result = typeof request === 'function'
        // eslint-disable-next-line no-unused-vars
        ? await request(config)
        : await httpRequest(requestApi, requestParam, config);

      state.value = 'success';
      return result;
    } catch (error) {
      state.value = 'fail';
      return error;
    } finally {
      console.log('request end');
    }
  };

  // 取消请求
  const onCancel = () => {
    cancelSource.value?.cancel();
  };

  // 自动重试
  const retryTimer = ref<any>(null);
  const onAutoRetry = () => {
    isRetrying.value = true;
    retryTimer.value = setInterval(() => {
      if (retryCount.value <= 0 || state.value !== 'fail') {
        stopRetry();
        return;
      }
      --retryCount.value;
      onRefresh();
    }, retryInterval);
  };
  const stopRetry = () => {
    isRetrying.value = false;
    retryCount.value = maxRetryCount;
    clearInterval(retryTimer.value);
  };
  watch(state, (newState) => {
    if (newState === 'fail' && !isRetrying.value) {
      onAutoRetry();
    }
  });

  const {
    result: response,
    refresh: onRefresh,
    isLoading
  } = usePromise(onLoad, null, { immediate: false, clearOnRefresh: true });

  return {
    onRefresh,
    onCancel,
    stopRetry,
    response,
    state,
    isRetrying,
    retryCount,
    isLoading
  };
}
